import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, AlertTriangle, CheckCircle, Clock, X, Check,
  RefreshCw, Package, Users, Zap, ChevronDown, ChevronUp,
  AlertCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  brouillon: { label: 'Brouillon', color: '#9CA3AF' },
  signe: { label: 'Signé', color: 'var(--admin-amber)' },
  acompte_paye: { label: 'Acompte payé', color: '#F97316' },
  confirme: { label: 'Confirmé ✓', color: '#22C55E' },
  valide_admin: { label: 'Validé', color: '#22C55E' },
  annule: { label: 'Annulé', color: 'var(--admin-red)' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((new Date() - new Date(iso)) / 1000);
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

export default function AdminDisponibilites() {
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState({});
  const [calendarData, setCalendarData] = useState({ events: [], conflicts: [] });
  const [activeTab, setActiveTab] = useState('validations');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const [pend, st, cal] = await Promise.all([
        fetch(`${BACKEND}/api/admin/reservations/pending`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/reservations/stats`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/reservations/calendar`, { headers }).then(r => r.json()),
      ]);
      setPending(pend.pending || []);
      setStats(st || {});
      setCalendarData(cal || { events: [], conflicts: [] });
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = async (quoteId) => {
    setProcessing(quoteId);
    try {
      const res = await fetch(`${BACKEND}/api/admin/reservations/${quoteId}/valider`, {
        method: 'POST', headers,
        body: JSON.stringify({ notes: adminNotes[quoteId] || '' }),
      });
      if (!res.ok) throw new Error();
      toast.success('Réservation validée ! Matériel bloqué.');
      setPending(prev => prev.filter(p => p.id !== quoteId));
    } catch {
      toast.error('Erreur lors de la validation');
    } finally {
      setProcessing(null);
    }
  };

  const refuse = async (quoteId) => {
    if (!window.confirm('Refuser cette réservation ?')) return;
    setProcessing(quoteId);
    try {
      await fetch(`${BACKEND}/api/admin/reservations/${quoteId}/refuser`, {
        method: 'POST', headers,
        body: JSON.stringify({ raison: adminNotes[quoteId] || '' }),
      });
      toast.success('Réservation refusée. Matériel libéré.');
      setPending(prev => prev.filter(p => p.id !== quoteId));
    } catch {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const TABS = [
    { id: 'validations', label: 'À valider', icon: Clock, badge: pending.length },
    { id: 'conflicts', label: 'Conflits', icon: AlertTriangle, badge: calendarData.conflicts?.length || 0 },
    { id: 'calendar', label: 'Calendrier', icon: Calendar, badge: null },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Planning</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Disponibilités</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Calendrier des disponibilités produits</p>
      </div>

      {/* Actions */}
      <div className="flex items-start justify-end mb-6 flex-wrap gap-4">
        <button onClick={() => load(false)} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'À valider', value: stats.en_attente_validation || 0, color: '#F97316', icon: Clock },
          { label: 'Confirmées', value: stats.confirmes || 0, color: '#22C55E', icon: CheckCircle },
          { label: 'Ce week-end', value: stats.ce_weekend || 0, color: 'var(--admin-teal)', icon: Calendar },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="card-premium p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} style={{ color: s.color }} />
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
              </div>
              <p className="font-display text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Légende statuts dispo */}
      <div className="flex flex-wrap gap-3 mb-5 p-3 rounded-2xl" style={{ background: 'var(--admin-bg)' }}>
        {[['#22C55E', '🟢', 'Disponible'], ['#F59E0B', '🟡', 'En demande (devis non payé)'], ['#F97316', '🟠', 'Acompte payé — contrôle requis'], ['#EF4444', '🔴', 'Réservé — validé admin'], ['#DC2626', '⛔', 'Indisponible']].map(([color, emoji, label]) => (
          <div key={label} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--admin-muted)' }}>
            <span>{emoji}</span> <span style={{ color }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-5" style={{ borderColor: 'var(--admin-border)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all"
              style={{
                borderColor: activeTab === tab.id ? '#D4AF37' : 'transparent',
                color: activeTab === tab.id ? '#D4AF37' : 'var(--text-muted)',
                background: 'none', cursor: 'pointer',
              }}>
              <Icon size={15} />
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={{ background: tab.id === 'conflicts' ? 'rgba(239,68,68,0.12)' : 'rgba(212,175,55,0.12)', color: tab.id === 'conflicts' ? '#EF4444' : '#C58B19' }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ══ VALIDATIONS ══════════════════════════════════════════════════ */}
      {activeTab === 'validations' && (
        <div className="space-y-3">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />)
          ) : pending.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={40} style={{ color: '#22C55E', margin: '0 auto 12px' }} />
              <p className="font-semibold" style={{ color: 'var(--admin-text)' }}>Aucune réservation en attente</p>
              <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Toutes les réservations sont traitées !</p>
            </div>
          ) : (
            pending.map(res => {
              const isOpen = expanded === res.id;
              const statusConf = STATUS_COLORS[res.statut] || { label: res.statut, color: '#888' };
              return (
                <div key={res.id} className="rounded-2xl overflow-hidden"
                  style={{
                    border: `1px solid ${res.has_conflicts ? 'rgba(239,68,68,0.4)' : res.statut === 'acompte_paye' ? 'rgba(249,115,22,0.4)' : 'var(--border)'}`,
                    background: '#FFFFFF',
                    boxShadow: res.has_conflicts ? '0 4px 16px rgba(239,68,68,0.1)' : 'none',
                  }}>
                  <button onClick={() => setExpanded(isOpen ? null : res.id)}
                    className="w-full flex items-center gap-4 p-4 text-left"
                    style={{ cursor: 'pointer', background: 'none', border: 'none' }}>

                    {/* Icône statut */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${statusConf.color}15` }}>
                      {res.has_conflicts
                        ? <AlertTriangle size={18} style={{ color: 'var(--admin-red)' }} />
                        : <Clock size={18} style={{ color: statusConf.color }} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{res.client_nom}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: `${statusConf.color}15`, color: statusConf.color }}>
                          {statusConf.label}
                        </span>
                        {res.has_conflicts && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                            style={{ background: 'var(--admin-red-dim)', color: 'var(--admin-red)' }}>
                            ⚠️ {res.conflicts.length} conflit{res.conflicts.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                        {res.date_evenement} · {res.products.slice(0,2).map(p => p.nom).join(', ')}
                        {res.nb_products > 2 ? ` +${res.nb_products - 2}` : ''}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <p className="text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>{(res.total || 0).toFixed(0)}€</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Acompte : {(res.acompte || 0).toFixed(0)}€</p>
                    </div>
                    {isOpen ? <ChevronUp size={16} style={{ color: 'var(--admin-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--admin-muted)' }} />}
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--admin-border)' }}>
                      <div className="pt-4 grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>Détails réservation</p>
                          <div className="space-y-1.5 text-xs" style={{ color: 'var(--admin-muted)' }}>
                            <p>📅 Date : <strong style={{ color: 'var(--admin-text)' }}>{res.date_evenement}{res.date_end && res.date_end !== res.date_evenement ? ` → ${res.date_end}` : ''}</strong></p>
                            <p>📧 Email : <a href={`mailto:${res.client_email}`} style={{ color: 'var(--admin-teal)' }}>{res.client_email}</a></p>
                            {res.client_telephone && <p>📞 Tél : {res.client_telephone}</p>}
                            <p>🎪 Produits ({res.nb_products}) :</p>
                            {res.products.map((p, i) => (
                              <p key={i} className="ml-4">• {p.nom}</p>
                            ))}
                            <p>💰 Total : <strong style={{ color: 'var(--admin-gold)' }}>{(res.total || 0).toFixed(2)}€</strong></p>
                            <p>✅ Acompte : <strong style={{ color: 'var(--admin-green)' }}>{(res.acompte || 0).toFixed(2)}€</strong></p>
                          </div>

                          {/* Conflits */}
                          {res.conflicts.length > 0 && (
                            <div className="mt-3 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
                              <p className="text-xs font-bold mb-1" style={{ color: 'var(--admin-red)' }}>⚠️ Conflits détectés</p>
                              {res.conflicts.map((c, i) => (
                                <p key={i} className="text-xs" style={{ color: '#DC2626' }}>
                                  • {c.product_nom} — conflit avec {c.conflict_with} ({c.conflict_client})
                                </p>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>Notes admin</p>
                          <textarea rows={3}
                            value={adminNotes[res.id] || ''}
                            onChange={e => setAdminNotes(prev => ({ ...prev, [res.id]: e.target.value }))}
                            placeholder="Notes internes (optionnel)..."
                            className="w-full px-3 py-2 rounded-xl text-xs mb-3"
                            style={{ border: '1px solid var(--admin-border)', background: 'var(--admin-bg)', color: 'var(--admin-text)', outline: 'none', resize: 'none' }}
                          />

                          <div className="flex gap-2">
                            <button
                              onClick={() => validate(res.id)}
                              disabled={processing === res.id}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold"
                              style={{ background: '#22C55E', color: 'var(--admin-text)', border: 'none', cursor: 'pointer', opacity: processing === res.id ? 0.7 : 1 }}>
                              <Check size={14} />
                              {processing === res.id ? 'En cours…' : 'Valider — Bloquer le matériel'}
                            </button>
                            <button
                              onClick={() => refuse(res.id)}
                              disabled={processing === res.id}
                              className="px-3 py-2.5 rounded-xl text-xs font-bold"
                              style={{ background: 'rgba(239,68,68,0.08)', color: 'var(--admin-red)', border: '1px solid rgba(239,68,68,0.2)', cursor: 'pointer' }}>
                              <X size={14} />
                            </button>
                          </div>

                          <p className="text-xs mt-2" style={{ color: 'var(--admin-muted)' }}>
                            Valider = le produit passe en ROUGE (réservé) sur le calendrier.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ══ CONFLITS ══════════════════════════════════════════════════════ */}
      {activeTab === 'conflicts' && (
        <div>
          {calendarData.conflicts?.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={40} style={{ color: '#22C55E', margin: '0 auto 12px' }} />
              <p className="font-semibold" style={{ color: 'var(--admin-text)' }}>Aucun conflit détecté</p>
              <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Toutes les réservations sont cohérentes.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {calendarData.conflicts.map((c, i) => (
                <div key={i} className="card-premium p-4"
                  style={{ borderLeft: '3px solid #EF4444' }}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={18} style={{ color: 'var(--admin-red)', flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p className="font-bold text-sm mb-1" style={{ color: 'var(--admin-text)' }}>
                        Conflit sur : {c.product_nom}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                        📅 Chevauchement : {c.overlap_start} → {c.overlap_end}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--admin-red)' }}>
                        Devis 1 : {c.quote_1?.slice(-8)} | Devis 2 : {c.quote_2?.slice(-8)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ CALENDRIER ════════════════════════════════════════════════════ */}
      {activeTab === 'calendar' && (
        <div>
          <div className="space-y-2">
            {calendarData.events?.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--admin-muted)' }}>Aucun événement à afficher</p>
            ) : (
              calendarData.events.map((ev, i) => {
                const sc = STATUS_COLORS[ev.statut] || { label: ev.statut, color: '#888' };
                return (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-2xl card-premium">
                    <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: ev.color || sc.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{ev.client_nom}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                        📅 {ev.date_start}{ev.date_end !== ev.date_start ? ` → ${ev.date_end}` : ''} · {ev.products.join(', ')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `${sc.color}15`, color: sc.color }}>
                        {sc.label}
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--admin-gold)' }}>{(ev.total || 0).toFixed(0)}€</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
